import React, { FCWithChildren } from 'react';
import { useRouter } from 'next/router';
import { NegotiationProvider } from '@context/negotiation.context';
import { useContextSelector } from 'use-context-selector';
import { ModalContext } from '@context/modal.context';
import { NotificationTab, Notification } from '@interfaces/models/notification';
import { NegotiationsService } from '@services/negotiations-service';
import { Negotiation } from '@interfaces/models/negotiation/negotiation';
import { getNegotiationIdFromUrl, getOrigin } from '@helpers/utils/mmao';
import { AnalyticsPayloadType } from '@interfaces/models/internal/analytics-event';
import { useAnalytics } from '@context/analytics/analytics.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import dynamic from 'next/dynamic';
import useUser from '@hooks/user/use-user';
import { NegotiationDialogStyles } from '@constants/negotiation';
import { UseMutateFunction } from '@tanstack/react-query';
import { NOTIFICATION_TAB_UPDATES, NOTIFICATION_TAB_MESSAGES } from '@constants/notification';
import useNotificationCountQueryV2 from '@hooks/notifications-v2/use-notification-count-query-v2';
import { ReadNotificationsPayload } from '@hooks/notifications-v2/use-notification-feed-query-v2';
import { constructNotificationTrackingPayload } from '@helpers/utils/notification/notification-utils';
import usePageType from '@hooks/use-page-type';
import styles from '../notification-card-v2/notification-card-v2.module.scss';

const NegotiationModal = dynamic(
  () => import('@components/organisms/negotiation/negotiation-modal/negotiation-modal').then((x) => x.NegotiationModal),
  {
    ssr: false,
  },
);
// TODO: https://gitlab.vestiairecollective.com/web/apps/fenx/-/merge_requests/4649
// Until we replace the legacy URLs with FENX URLs in certain campaigns, we need to support legacy page URLs from NC clicks to prevent broken pages when redirecting from a FENX page to a legacy page.

// TCRM-4750: We have a price drop campaign that redirects to the "Drop Price" FENX page but until we have all the URLs updated, we need to support the legacy URL.
const transformLinkPath = (linkPath) => {
  if (linkPath?.startsWith('/members/profile-')) {
    // Transform to /profile/id/
    const id = linkPath?.split('-')[1].split('.')[0];
    return `/profile/${id}/`;
  } else if (linkPath?.includes('id_produit=') && !linkPath?.includes('/drop-the-price/?id_produit=')) {
    // 48H campaign, Price Drop campaign
    // Transform to /p/id.shtml
    const match = linkPath.match(/id_produit=(\d+)/);
    const id = match ? match[1] : null;
    return id ? `/p/${id}.shtml` : '/';
  } else if (linkPath?.includes('/sell-clothes-online.shtml')) {
    return '/sell-clothes-online/';
  }
  return linkPath;
};

type NotificationDetailsProps = {
  notification: Notification;
  selectedTab: NotificationTab;
  onClick?: () => void;
  readNotification?: UseMutateFunction<void, void, ReadNotificationsPayload, unknown>;
  position?: number;
};

const NotificationDetailsV2: FCWithChildren<NotificationDetailsProps> = (props): React.JSX.Element => {
  const { notification, selectedTab, children, readNotification, position } = props;
  const { sendEvent } = useAnalytics();
  const { user } = useUser();
  const router = useRouter();
  const openModal = useContextSelector(ModalContext, (v) => v.openModal);
  const { pageType } = usePageType();

  const link =
    notification.productLink?.path || notification.productLink?.meta
      ? notification.productLink
      : notification.profileLink;
  const openChatFromLike = notification.eventType === 'article_like_chat';
  const notificationMeta = link.meta;
  const notificationProductPath = notification.productLink.path;
  const isFpdEnabled =
    notificationProductPath.includes('negotiation_id=') && notificationProductPath.includes('showFpd=true');
  const getNegotiationIdFromProductUrl = isFpdEnabled
    ? getNegotiationIdFromUrl(`${getOrigin()}/${notificationProductPath}`)
    : null;
  // Details either act as a button in case of MMAO, or as a link in case of chat or alert.
  const negotiationId: Negotiation['id'] | null =
    notificationMeta?.type === 'negotiation' ? notificationMeta.data.id.toString() : getNegotiationIdFromProductUrl;
  const chatId: string | null = notificationMeta?.type === 'chat' ? notificationMeta.data.id.toString() : null;
  const { sendAnalyticEvent } = useAnalyticEvents('notification');
  const closeModal = useContextSelector(ModalContext, (v) => v.closeModal);
  const { resetAndUpdateNotificationFeed } = useNotificationCountQueryV2();

  const handleNotificationClickTracking = () => {
    // TODO: remove when notification migration is done.
    const tracking = {
      event: 'notifications',
      mnemonic: notification.eventType,
      notificationStatus: notification.status === 'read' ? 'read' : 'unread',
      notificationID: notification.id,
      notificationCategory: notification.category,
      chat_action_name: '',
      chat_context: '',
    };

    if (chatId) {
      tracking.chat_action_name = 'open_from_mmao';
      tracking.chat_context = user?.id === chatId.split('-')[0] ? 'mmao_buyer' : 'mmao_seller';
    }

    sendEvent({
      type: 'event',
      payload: { tracking } as AnalyticsPayloadType,
    });

    const trackingPayload = constructNotificationTrackingPayload(notification, selectedTab);

    sendAnalyticEvent('click_notification', {
      label: trackingPayload.screen_section,
      property: trackingPayload.type,
      value: trackingPayload.read_status,
      notification_id: trackingPayload.id,
      notification_type: notification.eventType,
      notification_screen_section: trackingPayload.screen_section,
      notification_read_status: trackingPayload.read_status,
      notification_position: String(position),
    });
  };

  const linkAttrResource =
    link.path.startsWith('https://') || link.path.startsWith('http://')
      ? link.path
      : `${getOrigin()}${
          !!chatId
            ? `/chat?id=${chatId}`
            : openChatFromLike
              ? `${link.path}&community=true`
              : transformLinkPath(link.path)
        }`;

  const onNegotiationButtonClick = async (showFlashPriceDrop: boolean) => {
    try {
      const negotiation: Negotiation = await NegotiationsService.getNegotiation(negotiationId, true);
      if (negotiation) {
        openModal(
          (modalDialogTitleId) => (
            <NegotiationProvider negotiation={negotiation}>
              <NegotiationModal
                onClose={closeModal}
                trackingFrom="mmao_ongoing_banner_notification"
                showFpdSection={showFlashPriceDrop}
                modalDialogTitleId={modalDialogTitleId}
              />
            </NegotiationProvider>
          ),
          {
            styles: {
              content: NegotiationDialogStyles,
            },
          },
        );
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`Error fetching negotiation with id ${negotiationId}`, e);
    }
  };

  const onClickOnNotificationCard = async () => {
    handleNotificationClickTracking();
    if (notification.status !== 'read') {
      await readNotification({
        notification,
        selectedTab,
      });
    }
    await resetAndUpdateNotificationFeed();
    if (!link.path) {
      return;
    }

    /**
     * TODO:
      https://vestiairecollective.atlassian.net/browse/TCRM-4450
      https://www.vestiairecollective.com/drop-the-price/?id_produit=id
      Currently, the campaign URL doesn't include an id_product. 
      For now, we'll redirect to the "My Items for Sale" page. 
      The proper fix will be to update the campaigns to include the product ID 
      and redirect to the "Drop Price" page.
     */

    // Until we replace the legacy URLs with FENX URLs in certain campaigns, we need to support legacy page URLs from NC clicks to prevent broken pages when redirecting from a FENX page to a legacy page.
    if (
      link.path?.includes('/c/') ||
      link.path?.includes('/shop/cart') ||
      link.path?.includes('/my-account.shtml') ||
      link.path?.includes('/mon-compte.shtml')
    ) {
      // Redirect to checkout or my items for sale drop price (legacy)
      if (typeof window !== 'undefined') {
        window.location.assign(link.path);
      }
    } else {
      // DSXP-1900: The PLP(CategoryPage/CampaignPage) won't refresh when clicked from a notification due to client-side routing not triggering a fetch.
      if (pageType === 'CategoryPage' || pageType === 'CampaignPage') {
        window.location.assign(linkAttrResource);
      } else {
        router.push(linkAttrResource);
      }
    }
  };

  const onButtonClick = async () => {
    handleNotificationClickTracking();
    if (notification.status === 'read') {
      await onNegotiationButtonClick(!!negotiationId);
    } else {
      await Promise.all([
        onNegotiationButtonClick(!!negotiationId),
        readNotification({
          notification,
          selectedTab,
        }),
      ]);
    }
  };

  if (!!negotiationId) {
    return (
      <button
        className={styles.notificationCard__productWrapper__details}
        onClick={onButtonClick}
        data-cy="notificationCard__details"
      >
        <div className={styles.notificationCard__productWrapper__detailsDescriptionWrapper}>{children}</div>
      </button>
    );
  }

  return (
    <>
      {selectedTab === NOTIFICATION_TAB_UPDATES && (
        <button
          className={styles.notificationCard__productWrapper__details}
          onClick={onClickOnNotificationCard}
          data-cy="notificationCard__details"
        >
          <div className={styles.notificationCard__productWrapper__detailsDescriptionWrapper}>{children}</div>
        </button>
      )}
      {selectedTab === NOTIFICATION_TAB_MESSAGES && (
        <button
          className={styles.notificationCard__profileWrapper__details}
          onClick={onClickOnNotificationCard}
          data-cy="notificationCard__details"
        >
          <div className={styles.notificationCard__profileWrapper__detailsDescriptionWrapper}>{children}</div>
        </button>
      )}
    </>
  );
};

export default NotificationDetailsV2;
